import { getFY, getFyNumber } from '../../utils/calculation';
import { numberColumn } from '../BulkEditTable/utils';
import { TType } from '../../hooks/useTranslation';
import { calculateTotal } from '../FinancesByFy/utils';
import { defaultRenderer, textColumn } from '../ProjectListTable/utils';
import produce from 'immer';
import accounting from 'accounting-js';
import {
  convertToEur,
  returnCountryObject,
  returnCurrencyYear,
} from '../../hooks/useLocalCurrency';

const getHeaderClassName = (i: number) =>
  i % 2 === 0 ? 'header-cyan' : 'header-blue';

const startFy = getFyNumber(getFY(-2));
const finalFy = getFyNumber(getFY(5));

const sumAllFYTotals = (params, id, eur) => {
  return Number(
    calculateTotal({
      landCost: params.current.getCellValue(id, `landcost${eur ? '-EUR' : ''}`),
      propertyInvestment: params.current.getCellValue(
        id,
        `propertyInvestment${eur ? '-EUR' : ''}`,
      ),
      retailInvestment: params.current.getCellValue(
        id,
        `retailInvestment${eur ? '-EUR' : ''}`,
      ),
      retailCost: params.current.getCellValue(
        id,
        `retailCost${eur ? '-EUR' : ''}`,
      ),
    }) ?? 0,
  ).toString();
};

const sumAllFYInvestmentTypesTotals = (params, id, startFy, finalFy, eur) => {
  let landCost = 0,
    propertyInvestment = 0,
    retailInvestment = 0,
    retailCost = 0;
  for (let fy = startFy; fy <= finalFy; fy++) {
    landCost += Number(
      params.current.getCellValue(id, `FY${fy}-landCost${eur ? '-EUR' : ''}`) ??
        0,
    );
    propertyInvestment += Number(
      params.current.getCellValue(
        id,
        `FY${fy}-propertyInvestment${eur ? '-EUR' : ''}`,
      ) ?? 0,
    );
    retailInvestment += Number(
      params.current.getCellValue(
        id,
        `FY${fy}-retailInvestment${eur ? '-EUR' : ''}`,
      ) ?? 0,
    );
    retailCost += Number(
      params.current.getCellValue(
        id,
        `FY${fy}-retailCost${eur ? '-EUR' : ''}`,
      ) ?? 0,
    );
  }
  return { landCost, propertyInvestment, retailInvestment, retailCost };
};

const returnFinancialYearsColumns = (t: TType, onChange, currencyRates) => {
  const currentFy = getFyNumber(getFY());
  const columns = [];
  for (let fy = startFy; fy <= finalFy; fy++) {
    const currencyObject = returnCurrencyYear(currencyRates, `FY${fy}`);
    const isPastFy = fy < currentFy;
    columns.push(
      ...[
        numberColumn(
          `FY${fy}-landCost`,
          `${t(`projects.properties.LandCG01`)}`,
          onChange,
          isPastFy,
          100,
          null,
          null,
          true,
          true,
        ),
        numberColumn(
          `FY${fy}-landCost-EUR`,
          `${t(`projects.properties.LandCG01`)} EUR`,
          () => {
            /*do nothing*/
          },
          true,
          100,
          (value, row) => {
            const currObj = returnCountryObject(
              currencyObject,
              row?.countryCode,
            );
            const lcValue = row?.[`FY${fy}-landCost`];
            return lcValue ? convertToEur(currObj, lcValue) : '0';
          },
          null,
          true,
          true,
        ),
        numberColumn(
          `FY${fy}-propertyInvestment`,
          `${t(`projects.properties.propertyInvestmentCG111`)}`,
          onChange,
          isPastFy,
          100,
          null,
          null,
          true,
          true,
        ),
        numberColumn(
          `FY${fy}-propertyInvestment-EUR`,
          `${t(`projects.properties.propertyInvestmentCG111`)} EUR`,
          () => {
            /*do nothing*/
          },
          true,
          100,
          (value, row) => {
            const currObj = returnCountryObject(
              currencyObject,
              row?.countryCode,
            );
            const lcValue = row?.[`FY${fy}-propertyInvestment`];
            return lcValue ? convertToEur(currObj, lcValue) : '0';
          },
          null,
          true,
          true,
        ),
        numberColumn(
          `FY${fy}-retailInvestment`,
          `${t(`projects.properties.retailInvestmentCG1214`)}`,
          onChange,
          isPastFy,
          100,
          null,
          null,
          true,
        ),
        numberColumn(
          `FY${fy}-retailInvestment-EUR`,
          `${t(`projects.properties.retailInvestmentCG1214`)} EUR`,
          () => {
            /*do nothing*/
          },
          true,
          100,
          (value, row) => {
            const currObj = returnCountryObject(
              currencyObject,
              row?.countryCode,
            );
            const lcValue = row?.[`FY${fy}-retailInvestment`];
            return lcValue ? convertToEur(currObj, lcValue) : '0';
          },
          null,
          true,
          false,
        ),
        numberColumn(
          `FY${fy}-retailCost`,
          `${t(`projects.properties.retailCostCG1516`)}`,
          onChange,
          isPastFy,
          100,
          null,
          null,
          true,
        ),
        numberColumn(
          `FY${fy}-retailCost-EUR`,
          `${t(`projects.properties.retailCostCG1516`)} EUR`,
          () => {
            /*do nothing*/
          },
          true,
          100,
          (value, row) => {
            const currObj = returnCountryObject(
              currencyObject,
              row?.countryCode,
            );
            const lcValue = row?.[`FY${fy}-retailCost`];
            return lcValue ? convertToEur(currObj, lcValue) : '0';
          },
          null,
          true,
          false,
        ),
        numberColumn(
          `FY${fy}`,
          `Total in FY`,
          () => {
            /*do nothing*/
          },
          true,
          100,
          (value, row) =>
            calculateTotal({
              landCost: row?.[`FY${fy}-landCost`],
              propertyInvestment: row?.[`FY${fy}-propertyInvestment`],
              retailInvestment: row?.[`FY${fy}-retailInvestment`],
              retailCost: row?.[`FY${fy}-retailCost`],
            }),
          'grayBg',
        ),
        numberColumn(
          `FY${fy}-EUR`,
          `Total in FY EUR`,
          () => {
            /*do nothing*/
          },
          true,
          100,
          (value, row) => {
            const currObj = returnCountryObject(
              currencyObject,
              row?.countryCode,
            );
            return convertToEur(
              currObj,
              calculateTotal({
                landCost: row?.[`FY${fy}-landCost`],
                propertyInvestment: row?.[`FY${fy}-propertyInvestment`],
                retailInvestment: row?.[`FY${fy}-retailInvestment`],
                retailCost: row?.[`FY${fy}-retailCost`],
              }),
            );
          },
          null,
          false,
          false,
        ),
      ],
    );
  }
  return columns;
};

const defaultInfoColumns = {
  headerName: 'Project Information',
  groupId: 'projectInformation',
  headerClassName: null,
  children: [
    { field: 'referenceId' },
    { field: 'businessUnitId' },
    { field: 'projectName' },
    { field: 'unitType' },
    { field: 'budgetType' },
  ],
};
export const returnColumnGroupingModel = (
  years = [getFyNumber(getFY(-2)), getFyNumber(getFY(5))],
  infoColumns = defaultInfoColumns,
) => {
  const currentFy = years[0];
  const finalFy = years[1];
  const columnModel = [
    infoColumns,
    {
      headerName: 'Total Financials',
      groupId: 'totalFinancials',
      headerClassName: null,
      children: [
        { field: 'total' },
        { field: 'total-EUR' },
        { field: 'landCost' },
        { field: 'landCost-EUR' },
        { field: 'propertyInvestment' },
        { field: 'propertyInvestment-EUR' },
        { field: 'retailInvestment' },
        { field: 'retailInvestment-EUR' },
        { field: 'retailCost' },
        { field: 'retailCost-EUR' },
      ],
    },
  ];
  for (let i = currentFy; i <= finalFy; i++) {
    columnModel.push({
      headerName: `FY${i}`,
      groupId: `FY${i}`,
      headerClassName: getHeaderClassName(i),
      children: [
        { field: `FY${i}-landCost` },
        { field: `FY${i}-propertyInvestment` },
        { field: `FY${i}-retailInvestment` },
        { field: `FY${i}-retailCost` },
        { field: `FY${i}` },
        { field: `FY${i}-landCost-EUR` },
        { field: `FY${i}-propertyInvestment-EUR` },
        { field: `FY${i}-retailInvestment-EUR` },
        { field: `FY${i}-retailCost-EUR` },
        { field: `FY${i}-EUR` },
      ],
    });
  }
  return columnModel;
};
export const getTableColumns = (
  t: TType,
  onChange,
  currencyRates?: unknown,
) => [
  textColumn('referenceId', 'Reference ID'),
  textColumn('businessUnitId', 'BU'),
  textColumn('projectName', t('ppm.columns.projectName'), 200),
  {
    field: 'unitType',
    headerName: t('ppm.columns.unitType'),
    aggregable: false,
    minWidth: 230,
    display: 'flex',
    valueGetter: (value) => (value ? t(`unitTypes.${value}`) : ''),
    renderCell: defaultRenderer,
  },
  {
    field: 'budgetType',
    headerName: 'Financial Type',
    minWidth: 160,
    groupable: false,
    aggregable: false,
    display: 'flex',
    valueFormatter: (value) => (value ? t(`budgetTypes.${value}`) : ''),
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value: params.formattedValue ?? params.value,
      }),
  },
  {
    field: 'total',
    headerName: 'Total investment',
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) => sumAllFYTotals(p, row?.id, false),
  },
  {
    field: 'total-EUR',
    headerName: 'Total investment EUR',
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) => sumAllFYTotals(p, row?.id, true),
  },
  {
    field: 'landCost',
    headerName: `${t(`projects.properties.LandCG01`)}`,
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) =>
      sumAllFYInvestmentTypesTotals(p, row?.id, startFy, finalFy, false)
        .landCost,
  },
  {
    field: 'landCost-EUR',
    headerName: `${t(`projects.properties.LandCG01`)} EUR`,
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) =>
      sumAllFYInvestmentTypesTotals(p, row?.id, startFy, finalFy, true)
        .landCost,
  },
  {
    field: 'propertyInvestment',
    headerName: `${t(`projects.properties.propertyInvestmentCG111`)}`,
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) =>
      sumAllFYInvestmentTypesTotals(p, row?.id, startFy, finalFy, false)
        .propertyInvestment,
  },
  {
    field: 'propertyInvestment-EUR',
    headerName: `${t(`projects.properties.propertyInvestmentCG111`)} EUR`,
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) =>
      sumAllFYInvestmentTypesTotals(p, row?.id, startFy, finalFy, true)
        .propertyInvestment,
  },
  {
    field: 'retailInvestment',
    headerName: `${t(`projects.properties.retailInvestment`)}`,
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) =>
      sumAllFYInvestmentTypesTotals(p, row?.id, startFy, finalFy, false)
        .retailInvestment,
  },
  {
    field: 'retailInvestment-EUR',
    headerName: `${t(`projects.properties.retailInvestment`)} EUR`,
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) =>
      sumAllFYInvestmentTypesTotals(p, row?.id, startFy, finalFy, true)
        .retailInvestment,
  },
  {
    field: 'retailCost',
    headerName: `${t(`projects.properties.retailCostCG1516`)}`,
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) =>
      sumAllFYInvestmentTypesTotals(p, row?.id, startFy, finalFy, false)
        .retailCost,
  },
  {
    field: 'retailCost-EUR',
    headerName: `${t(`projects.properties.retailCostCG1516`)} EUR`,
    minWidth: 60,
    groupable: false,
    aggregable: false,
    display: 'flex',
    renderCell: (params) =>
      defaultRenderer({
        ...params,
        value:
          params?.row?.budgetType !== 'BASELINE'
            ? accounting.formatMoney(params?.value, {
                precision: 0,
                symbol: '',
              })
            : 'N/A',
      }),
    valueGetter: (value, row, c, p) =>
      sumAllFYInvestmentTypesTotals(p, row?.id, startFy, finalFy, true)
        .retailCost,
  },
  ...returnFinancialYearsColumns(t, onChange, currencyRates),
];

export const invesmentsTypeEnum = {
  BUDGET: 'finances.projectBudget',
  FORECAST: 'finances.forecast',
  BASELINE: 'finances.baseline',
  RELEASED_APPROVED_BUDGET: 'finances.releasedAmount',
  ACTUALS: 'finances.actuals',
};

export const mapProjectsForTable = (projects) => {
  const formattedProjects = [];

  projects.forEach((project) => {
    const baseProject = {
      id: project.id,
      countryCode: project.countryCode,
      referenceId: project.referenceId,
      isep: project.isep,
      businessUnitId: project.businessUnitId,
      projectName: project.projectName,
      unitType: project.unitType,
      establishmentType: project.establishmentType,
      realEstateSISAN: project.realEstateSISAN,
    };

    const projectMap = {
      FORECAST: {
        budgetType: 'FORECAST',
        ...baseProject,
        id: `${project.id}-FORECAST`,
      },
      SUBMITTED_FORECAST: {
        budgetType: 'SUBMITTED_FORECAST',
        ...baseProject,
        id: `${project.id}-SUBMITTED_FORECAST`,
      },
      BASELINE: {
        budgetType: 'BASELINE',
        ...baseProject,
        id: `${project.id}-BASELINE`,
      },
      RELEASED_APPROVED_BUDGET: {
        budgetType: 'RELEASED_APPROVED_BUDGET',
        ...baseProject,
        id: `${project.id}-RELEASED_APPROVED_BUDGET`,
      },
      BUDGET: {
        budgetType: 'BUDGET',
        ...baseProject,
        id: `${project.id}-BUDGET`,
      },
      ACTUALS: {
        budgetType: 'ACTUALS',
        ...baseProject,
        id: `${project.id}-ACTUALS`,
      },
    };

    project.investments.forEach((investment) => {
      const fiscalYear = investment.fiscalYear;

      Object.keys(projectMap).forEach((type) => {
        const data = investment[type];
        if (data) {
          projectMap[type][`${fiscalYear}-landCost`] = data.landCost || 0;
          projectMap[type][`${fiscalYear}-propertyInvestment`] =
            data.propertyInvestment || 0;
          projectMap[type][`${fiscalYear}-retailInvestment`] =
            data.retailInvestment || 0;
          projectMap[type][`${fiscalYear}-retailCost`] = data.retailCost || 0;
        } else {
          projectMap[type][`${fiscalYear}-landCost`] = 0;
          projectMap[type][`${fiscalYear}-propertyInvestment`] = 0;
          projectMap[type][`${fiscalYear}-retailInvestment`] = 0;
          projectMap[type][`${fiscalYear}-retailCost`] = 0;
        }
      });
    });

    formattedProjects.push(...Object.values(projectMap));
  });
  return formattedProjects;
};

export const getDataFromTable = (projectId: string, fieldName: string) => {
  const type = projectId.split('-').pop();
  const fiscalYear = fieldName.split('-')[0];
  const field = fieldName.split('-')[1];
  const id = projectId.split('-').slice(0, -1).join('-');
  return {
    id,
    type,
    fiscalYear,
    field,
  };
};

const mapTableToProjects = (formattedProjects = []) => {
  const projectsMap = new Map();

  formattedProjects.forEach((formattedProject) => {
    const { id, businessUnitId, projectName, unitType, budgetType, ...rest } =
      formattedProject;

    const projectId = id.split('-').slice(0, -1).join('-');

    if (!projectsMap.has(projectId)) {
      projectsMap.set(projectId, {
        id: projectId,
        businessUnitId,
        projectName,
        unitType,
        investments: [],
      });
    }

    const project = projectsMap.get(projectId);

    const investmentData = {};
    Object.keys(rest).forEach((key) => {
      const [fiscalYear, costType] = key.split('-');
      if (!investmentData[fiscalYear]) {
        investmentData[fiscalYear] = {};
      }

      investmentData[fiscalYear][budgetType] =
        investmentData[fiscalYear][budgetType] || {};
      investmentData[fiscalYear][budgetType][costType] = rest[key];
    });

    Object.entries(investmentData).forEach(([fiscalYear, data]) => {
      let investment = project.investments.find(
        (inv) => inv.fiscalYear === fiscalYear,
      );

      if (!investment) {
        investment = { fiscalYear };
        project.investments.push(investment);
      }

      Object.entries(data).forEach(([budgetType, budgetData]) => {
        investment[budgetType] = { ...investment[budgetType], ...budgetData };
      });
    });
  });

  return projectsMap;
};

export const checkForFinancialsError = (financials) => {
  let error = false;
  financials.forEach((financial) => {
    Object.keys(financial).forEach((key) => {
      const [, costType] = key.split('-');
      if (costType === 'error' && financial[key]) {
        error = true;
      }
    });
  });
  return error;
};

export const reverseFinancialData = (projects, financialData) => {
  const investments = mapTableToProjects(financialData);
  return projects.map((project) => {
    const projectInvestments = investments.get(project.id);
    const newProject = { ...project };
    if (projectInvestments) {
      newProject.investments = produce(newProject.investments, (draft) => {
        draft.forEach((investment) => {
          const investmentData = projectInvestments.investments.find(
            (inv) => inv.fiscalYear === investment.fiscalYear,
          );
          if (investmentData) {
            Object.keys(investmentData).forEach((key) => {
              if (key === 'fiscalYear') {
                return;
              }
              if (!investment[key]) {
                investment[key] = {
                  ...investment[key],
                  landCost: 0,
                  propertyInvestment: 0,
                  retailInvestment: 0,
                  retailCost: 0,
                };
              }
              investment[key] = {
                ...investment[key],
                landCost: investmentData[key].landCost,
                propertyInvestment: investmentData[key].propertyInvestment,
                retailInvestment: investmentData[key].retailInvestment,
                retailCost: investmentData[key].retailCost,
              };
            });
          }
        });
      });
    }
    return newProject;
  });
};
export const financialCategories = [
  'BUDGET',
  'SUBMITTED_FORECAST',
  'BASELINE',
  'RELEASED_APPROVED_BUDGET',
  'ACTUALS',
  'FORECAST',
];

export const financialFilter = [
  { label: 'Project budget', value: 'BUDGET' },
  { label: 'Submitted Forecast', value: 'SUBMITTED_FORECAST' },
  { label: 'Baseline FY Frame', value: 'BASELINE' },
  { label: 'Release/Approved budget', value: 'RELEASED_APPROVED_BUDGET' },
  { label: 'Actuals', value: 'ACTUALS' },
  { label: 'Forecast', value: 'FORECAST' },
];

export const filterSelectedValues = (values, value) => {
  const set = new Set(values);
  if (set.has(value)) {
    set.delete(value);
    return [...set];
  }
  return [...values, value];
};
